import { useCallback, useMemo } from "react";
import { type TableColumnType } from "@ir/ui";
import { GetMasterDataRawRetailerAssociationsDTO } from "@src/domain/dto/masterData";
import type { MasterDataRawRetailerAssociation as MasterDataRawRetailerAssociationEntity } from "@src/domain/entities/masterData";
import { GetMasterDataRawRetailerAssociationsUseCase } from "@src/application/useCases/masterData";
import { MasterDataRawRetailerAssociationRepository } from "@src/infrastructure/repositories/masterData";
import { useMasterDataRetailers } from "@src/presentation/providers/MasterDataRetailersDataProvider";
import { masterDataRawRetailerAssocCacheKey } from "@src/presentation/providers/masterData/masterDataListCache";
import {
  MasterDataRetailerChildListModal,
  type MasterDataRetailerChildModalHostProps,
} from "./MasterDataRetailerChildListModal";
import { MasterDataRawRetailerAssociationAddEditForm } from "./MasterDataRawRetailerAssociationAddEditForm";
import {
  MASTER_DATA_COLUMN_VERTICAL_ALIGN,
  renderMasterDataTextCell,
} from "./masterDataTable.shared";
import {
  buildRawAssociationActionsColumn,
  indexRawAssociationsById,
  mapRawAssociationsToRows,
  type MasterDataRawRetailerAssociationRow,
} from "./masterDataRawRetailerAssociationTable.shared";
import { useMasterDataRawRetailerAssociationForm } from "./useMasterDataRawRetailerAssociationForm";
import { useMasterDataRetailerChildList } from "./useMasterDataRetailerChildList";

function buildColumns(
  onEdit: (entity: MasterDataRawRetailerAssociationEntity) => void,
  associationsById: Map<number, MasterDataRawRetailerAssociationEntity>,
): TableColumnType<MasterDataRawRetailerAssociationRow>[] {
  return [
    {
      title: "Raw Retailer Name",
      dataIndex: "rawRetailerName",
      key: "rawRetailerName",
      align: "left",
      verticalAlign: MASTER_DATA_COLUMN_VERTICAL_ALIGN,
      width: 280,
      cellRender: renderMasterDataTextCell,
    },
    buildRawAssociationActionsColumn(onEdit, associationsById),
  ];
}

export const MasterDataRawRetailerAssociationsModal = ({
  isOpen,
  retailerId,
  retailerName,
  onClose,
}: MasterDataRetailerChildModalHostProps) => {
  const { brandKey, refetch: refetchRetailers } = useMasterDataRetailers();
  const {
    page,
    setPage,
    pageSize,
    items,
    itemTotal,
    loading,
    error,
    handleSaved,
    resetOnClose,
  } = useMasterDataRetailerChildList<MasterDataRawRetailerAssociationEntity>({
    isOpen,
    retailerId,
    brandKey,
    loadErrorFallback: "Failed to load raw retailer associations",
    buildCacheKey: masterDataRawRetailerAssocCacheKey,
    invalidateScopes: ["raw-retailer-assoc-modal", "retailers"],
    fetchPage: (id, pageNumber, limit) =>
      new GetMasterDataRawRetailerAssociationsUseCase(
        new MasterDataRawRetailerAssociationRepository(),
      ).execute(
        new GetMasterDataRawRetailerAssociationsDTO({
          retailerId: id,
          search: null,
          page_number: pageNumber,
          page_size: limit,
        }),
      ),
    refetchParent: refetchRetailers,
  });

  const {
    form,
    formMode,
    isSaving,
    isFormSubmittable,
    setIsFormSubmittable,
    initialValues,
    lockedRetailerName,
    openCreate,
    openEdit,
    closeForm,
    handleSubmit,
  } = useMasterDataRawRetailerAssociationForm({
    onSaved: handleSaved,
    lockedRetailerId: retailerId,
    lockedRetailerName: retailerName,
  });

  const associationsById = useMemo(
    () => indexRawAssociationsById(items),
    [items],
  );
  const columns = useMemo(
    () => buildColumns(openEdit, associationsById),
    [openEdit, associationsById],
  );
  const dataSource = useMemo(() => mapRawAssociationsToRows(items), [items]);

  const handleClose = useCallback(() => {
    closeForm();
    resetOnClose();
    onClose();
  }, [closeForm, onClose, resetOnClose]);

  const formStrip =
    formMode != null ? (
      <MasterDataRawRetailerAssociationAddEditForm
        form={form}
        mode={formMode}
        initialValues={initialValues}
        lockedRetailerName={lockedRetailerName}
        isSaving={isSaving}
        isFormSubmittable={isFormSubmittable}
        onSubmit={handleSubmit}
        onCancel={() => closeForm()}
        onFormValidityChange={setIsFormSubmittable}
      />
    ) : null;

  return (
    <MasterDataRetailerChildListModal
      isOpen={isOpen}
      title={
        retailerName
          ? `Raw Retailer Associations — ${retailerName}`
          : "Raw Retailer Associations"
      }
      addButtonLabel="Add Association"
      emptyTitle="No raw retailer associations for this retailer."
      tableAriaLabel="Raw retailer associations"
      paginationAriaLabel="Raw retailer associations pagination"
      loading={loading}
      error={error}
      isFormOpen={formMode != null}
      formStrip={formStrip}
      columns={columns}
      dataSource={dataSource}
      page={page}
      pageSize={pageSize}
      itemTotal={itemTotal}
      onPageChange={setPage}
      onAdd={openCreate}
      onClose={handleClose}
    />
  );
};
